import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, Bike, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const HERO_IMAGE = 'https://customer-assets-39nsmqrw.emergentagent.net/job_b41f1f79-1fbd-4a20-b119-53b1dfd6f16a/artifacts/5as82tdr_IMG_4642.webp';
const DISPLAY_FONT = { fontFamily: "'Bebas Neue', 'Oswald', sans-serif" };

const ROUTE_TYPE_LABEL = { off_road: 'Off Road', trail: 'Trail (Mixto)', on_road: 'On Road', encuentro: 'Encuentro' };
const CATEGORY_LABEL = { easy: 'Fácil', intermediate: 'Intermedio', expert: 'Experto', sin_conduccion: 'Sin Conducción' };
const CATEGORY_CLASS = {
  easy: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  intermediate: 'bg-amber-100 text-amber-700 border-amber-300',
  sin_conduccion: 'bg-sky-100 text-sky-700 border-sky-300',
  expert: 'bg-red-100 text-red-700 border-red-300'
};
const STYLE_LABEL = { slowride: 'SlowRide/Mototurismo', insaneride: 'InsaneRide/Resistencia', noride: 'NoRide/Amistad' };

function posterSrc(poster) {
  return poster.startsWith('http') ? poster : `${process.env.REACT_APP_BACKEND_URL}/api${poster}`;
}

function EventCard({ event, navigate }) {
  const completed = event.status === 'completed';
  return (
    <article
      className={`group relative flex flex-col rounded-xl overflow-hidden border transition-all duration-300 ${
        completed
          ? 'bg-gray-50 border-gray-200 opacity-75 hover:opacity-100 text-gray-600'
          : 'bg-white border-gray-200 hover:border-orange-400 hover:shadow-xl hover:-translate-y-1 text-gray-900'
      }`}
      data-testid={`event-card-${event.id}`}
    >
      {event.poster_image && !completed && (
        <div className="relative h-48 overflow-hidden bg-gray-100">
          <img
            src={posterSrc(event.poster_image)}
            alt={event.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            data-testid={`event-poster-${event.id}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
        </div>
      )}

      <div className="p-5 space-y-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-2xl leading-none tracking-wide text-gray-900" style={DISPLAY_FONT} data-testid={`event-title-${event.id}`}>
            {event.title}
          </h3>
          <div className="flex gap-1.5 flex-wrap justify-end">
            {completed && <Badge className="bg-gray-200 text-gray-600 border border-gray-300 hover:bg-gray-200">REALIZADO</Badge>}
            {event.status === 'blocked' && <Badge className="bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-100">BLOQUEADO</Badge>}
            {event.requires_payment && (
              <Badge className="bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-100" data-testid={`event-price-badge-${event.id}`}>
                {event.price}€
              </Badge>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed" data-testid={`event-description-${event.id}`}>
          {event.description}
        </p>

        <div className="space-y-2 pt-1 text-sm text-gray-600">
          {event.organizer && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-orange-500 shrink-0" />
              <span className="font-medium text-gray-800">{event.organizer}</span>
            </div>
          )}
          <div className="flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-orange-500" />
              <span data-testid={`event-date-${event.id}`}>{event.start_date}</span>
            </span>
            <span className="flex items-center gap-2">
              <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
              <span>{event.end_date}</span>
            </span>
          </div>
          {event.target_km > 0 && (
            <div className="flex items-center gap-2 text-emerald-700 font-semibold" data-testid={`event-target-km-${event.id}`}>
              <ArrowRight className="w-4 h-4" />
              Objetivo: {Number(event.target_km).toLocaleString('es-ES')} km
            </div>
          )}
          <div className="flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-500" />
              <span data-testid={`event-participants-${event.id}`}>Máx. {event.max_participants}</span>
            </span>
            <span className="flex items-center gap-2 text-emerald-600 font-semibold">
              <Users className="w-4 h-4" />
              Plazas libres: {event.available_spots !== undefined ? event.available_spots : event.max_participants}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap pt-1">
          {event.route_type && (
            <Badge className="bg-orange-100 text-orange-700 border border-orange-300 text-[11px] uppercase tracking-wider hover:bg-orange-100">
              {ROUTE_TYPE_LABEL[event.route_type]}
            </Badge>
          )}
          {event.category && (
            <Badge className={`border text-[11px] uppercase tracking-wider ${CATEGORY_CLASS[event.category] || CATEGORY_CLASS.expert}`}>
              {CATEGORY_LABEL[event.category]}
            </Badge>
          )}
          {STYLE_LABEL[event.style] && (
            <Badge className="bg-violet-100 text-violet-700 border border-violet-300 text-[11px] uppercase tracking-wider hover:bg-violet-100">
              {STYLE_LABEL[event.style]}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex gap-2 p-5 pt-0">
        <Button
          onClick={() => navigate(`/events/${event.id}`)}
          className="flex-1 h-12 bg-white hover:bg-gray-100 text-gray-800 font-semibold border border-gray-300 uppercase tracking-wider text-sm"
          data-testid={`view-event-btn-${event.id}`}
        >
          Ver detalles
        </Button>
        {!completed && (
          <Button
            onClick={() => navigate(`/events/${event.id}#registro`)}
            className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wider text-sm shadow-md hover:shadow-orange-500/30 disabled:opacity-40"
            data-testid={`register-event-btn-${event.id}`}
            disabled={event.status === 'blocked'}
          >
            Inscripción
          </Button>
        )}
      </div>
    </article>
  );
}

function Hero({ user, upcomingCount }) {
  const firstName = user?.full_name?.split(' ')[0];
  return (
    <section
      className="relative overflow-hidden rounded-2xl bg-zinc-950 shadow-2xl mb-12 min-h-[280px] sm:min-h-[340px] flex items-end"
      data-testid="hero-section"
    >
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Strategos Overland" className="w-full h-full object-cover object-center opacity-50 contrast-125 saturate-[1.1]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/40" />
        <div
          className="absolute inset-0 opacity-[0.12] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }}
        />
      </div>

      <div className="relative z-10 p-6 sm:p-10 md:p-12 max-w-3xl space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 backdrop-blur-md text-orange-400 text-[11px] font-mono uppercase tracking-[0.2em]">
          <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
          Panel de piloto
        </div>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-wide text-white" style={DISPLAY_FONT}>
          {firstName ? <>Hola, {firstName}.<br /></> : null}
          Tu próximo desafío <span className="text-orange-500">empieza aquí</span>
        </h1>
        <p className="text-base md:text-lg text-zinc-300 max-w-xl leading-relaxed">
          {upcomingCount > 0
            ? <>Hay <span className="text-white font-semibold">{upcomingCount} {upcomingCount === 1 ? 'experiencia abierta' : 'experiencias abiertas'}</span> esperándote. Consulta los recorridos y asegura tu plaza.</>
            : 'Estamos preparando las próximas experiencias. Muy pronto podrás inscribirte.'}
        </p>
      </div>
    </section>
  );
}

function SectionTitle({ children, muted, testId }) {
  return (
    <h2
      className={`text-3xl sm:text-4xl tracking-wider uppercase mb-8 flex items-center gap-3 border-l-4 pl-3 ${muted ? 'text-gray-400 border-gray-300' : 'text-gray-900 border-orange-500'}`}
      style={DISPLAY_FONT}
      data-testid={testId}
    >
      <Calendar className={`w-7 h-7 ${muted ? 'text-gray-400' : 'text-orange-500'}`} />
      {children}
    </h2>
  );
}

export default function Home({ user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data);
      } catch (error) {
        toast.error('Error al cargar eventos');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-gray-50 text-gray-500 flex items-center justify-center">Cargando eventos...</div>;
  }

  const upcomingEvents = events
    .filter(e => e.status === 'active' || e.status === 'blocked')
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  const completedEvents = events
    .filter(e => e.status === 'completed')
    .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 py-8 sm:py-12 px-4 sm:px-6 lg:px-8 glove-friendly">
      <div className="max-w-7xl mx-auto">
        <Hero user={user} upcomingCount={upcomingEvents.length} />

        {upcomingEvents.length > 0 && (
          <div className="mb-16">
            <SectionTitle testId="upcoming-events-title">Próximos eventos</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="upcoming-events-grid">
              {upcomingEvents.map(event => <EventCard key={event.id} event={event} navigate={navigate} />)}
            </div>
          </div>
        )}

        {completedEvents.length > 0 && (
          <div>
            <SectionTitle muted testId="completed-events-title">Eventos realizados</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="completed-events-grid">
              {completedEvents.map(event => <EventCard key={event.id} event={event} navigate={navigate} />)}
            </div>
          </div>
        )}

        {events.length === 0 && (
          <div className="max-w-md mx-auto text-center py-16 rounded-xl border border-gray-200 bg-white" data-testid="no-events-message">
            <Bike className="w-14 h-14 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No hay eventos disponibles en este momento</p>
          </div>
        )}
      </div>
    </div>
  );
}
